<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8 pb-12">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <!-- Denied state -->
                <zen-denied-state
                    v-if="!canView && !hasPlanFeature('advanced_reporting')"
                    class="mt-4"
                    title="Rollout progress restricted"
                    message="Rollout progress needs a higher plan — no sales call, just upgrade in Billing."
                />
                <zen-denied-state
                    v-else-if="!canView"
                    class="mt-4"
                    title="Rollout progress restricted"
                    message="You don't have permission to view rollout progress. Contact your account administrator to request access."
                />

                <template v-if="canView">
                    <div class="mt-2 sm:mt-4 space-y-4">
                        <div class="sm:flex sm:items-center">
                            <div class="sm:flex">
                                <h2 class="text-xl font-medium text-taupe-800">
                                    Rollout Progress
                                </h2>
                            </div>
                        </div>
                        <project-change-notice
                            :project-key="current.projectKey"
                            :auto-refresh-on="['target-rollout-updated']"
                            @refresh="refresh"
                        />

                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="rollout-progress-filter-flag"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Flag
                                </label>
                                <select
                                    id="rollout-progress-filter-flag"
                                    v-model="filters.flagKey"
                                    data-testid="rollout-progress-filter-flag"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All flags</option>
                                    <option
                                        v-for="flag in flags"
                                        :key="flag.key"
                                        :value="flag.key"
                                    >
                                        {{ flag.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="rollout-progress-filter-status"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Status
                                </label>
                                <select
                                    id="rollout-progress-filter-status"
                                    v-model="filters.status"
                                    data-testid="rollout-progress-filter-status"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">
                                        Active &amp; Paused
                                    </option>
                                    <option value="active">Active</option>
                                    <option value="paused">Paused</option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="rollout-progress-apply-filters"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load rollout progress. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading">
                            <div
                                v-if="activeRollouts.length === 0"
                                class="bg-white shadow-sm rounded-lg"
                            >
                                <zen-empty-table-message
                                    compact
                                    data-testid="rollout-progress-empty"
                                    icon="ZenIconChartBar"
                                >
                                    No active or paused rollouts found matching
                                    your filters.
                                </zen-empty-table-message>
                            </div>

                            <div
                                v-for="rollout in activeRollouts"
                                :key="rollout.target_ulid"
                                class="rounded-md border border-taupe-200 bg-white p-4 space-y-4"
                                :data-testid="
                                    'rollout-progress-card-' +
                                    rollout.target_ulid
                                "
                            >
                                <!-- Header -->
                                <div
                                    class="flex items-center justify-between flex-wrap gap-y-2"
                                >
                                    <div
                                        class="flex items-center space-x-2 min-w-0"
                                    >
                                        <span
                                            class="text-sm font-medium text-taupe-900 truncate"
                                        >
                                            {{
                                                rollout.flag_name ||
                                                rollout.flag_key ||
                                                'Unknown flag'
                                            }}
                                        </span>
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                            :class="
                                                modeBadgeClass(rollout.mode)
                                            "
                                        >
                                            {{ formatMode(rollout.mode) }}
                                        </span>
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                            :class="
                                                statusBadgeClass(rollout.status)
                                            "
                                        >
                                            {{ formatStatus(rollout.status) }}
                                        </span>
                                    </div>
                                    <span class="text-sm text-taupe-500">
                                        {{ rollout.environment_key }}
                                    </span>
                                </div>

                                <!-- Progress bar -->
                                <div>
                                    <div
                                        class="flex items-center justify-between text-sm text-taupe-600 mb-1"
                                    >
                                        <span>
                                            {{ rollout.current_percentage }}% of
                                            {{ rollout.target_percentage }}%
                                        </span>
                                        <span
                                            v-if="rollout.next_advance_at"
                                            class="text-taupe-400"
                                        >
                                            Next advance:
                                            <zen-date-time
                                                :value="rollout.next_advance_at"
                                                human
                                            />
                                        </span>
                                    </div>
                                    <div
                                        class="w-full bg-taupe-100 rounded-full h-2.5"
                                    >
                                        <div
                                            class="bg-sky-500 h-2.5 rounded-full"
                                            :style="{
                                                width:
                                                    rollout.current_percentage +
                                                    '%',
                                            }"
                                        />
                                    </div>
                                </div>

                                <!-- Step chart for automatic rollouts -->
                                <div
                                    v-if="rollout.stages"
                                    class="flex items-center flex-wrap gap-2"
                                    data-testid="rollout-progress-stages"
                                >
                                    <span
                                        v-for="stage in rollout.stages"
                                        :key="stage"
                                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                        :class="
                                            stage <= rollout.stage
                                                ? 'bg-sky-100 text-sky-800'
                                                : 'bg-taupe-100 text-taupe-400'
                                        "
                                    >
                                        {{ stage }}%
                                    </span>
                                </div>

                                <!-- Stage history timeline -->
                                <div>
                                    <h2
                                        class="text-sm font-medium text-taupe-700 mb-2"
                                    >
                                        Timeline
                                    </h2>
                                    <ul
                                        v-if="rollout.stage_history.length > 0"
                                        class="divide-y divide-taupe-100"
                                    >
                                        <li
                                            v-for="(
                                                entry, index
                                            ) in rollout.stage_history"
                                            :key="index"
                                            class="flex items-center justify-between py-1.5 text-sm"
                                        >
                                            <span class="text-taupe-800">
                                                {{
                                                    formatRolloutAction(
                                                        entry.rollout_action
                                                    )
                                                }}
                                                <span
                                                    v-if="
                                                        entry.percentage !==
                                                        null
                                                    "
                                                    class="text-taupe-500"
                                                >
                                                    &rarr;
                                                    {{ entry.percentage }}%
                                                </span>
                                            </span>
                                            <span
                                                class="flex items-center space-x-3 text-taupe-400"
                                            >
                                                <span>{{
                                                    entry.actor_name
                                                }}</span>
                                                <zen-date-time
                                                    :value="entry.created_at"
                                                    human
                                                />
                                            </span>
                                        </li>
                                    </ul>
                                    <div v-else class="text-sm text-taupe-500">
                                        No stage changes recorded yet.
                                    </div>
                                </div>

                                <!-- Evaluations per stage -->
                                <div
                                    v-if="
                                        rollout.evaluations_by_stage.length > 0
                                    "
                                >
                                    <h2
                                        class="text-sm font-medium text-taupe-700 mb-2"
                                    >
                                        Evaluations by Stage
                                    </h2>
                                    <ul class="divide-y divide-taupe-100">
                                        <li
                                            v-for="(
                                                stage, index
                                            ) in rollout.evaluations_by_stage"
                                            :key="index"
                                            class="flex items-center justify-between py-1.5 text-sm"
                                        >
                                            <span class="text-taupe-800"
                                                >{{ stage.percentage }}%</span
                                            >
                                            <span
                                                class="font-medium text-taupe-500"
                                                >{{
                                                    stage.evaluation_count
                                                }}
                                                evaluations</span
                                            >
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </template>

                        <zen-pagination
                            v-if="!loading"
                            :page="pagination.page"
                            :total-pages="pagination.total_pages"
                            :total="pagination.total"
                            @change="changePage"
                        />

                        <div v-if="!loading" class="text-sm pb-10">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </div>
                </template>
            </div>
        </section>
    </main>
</template>

<script>
import { computed } from 'vue'

import ReportService from '@/services/report.service'
import FlagService from '@/services/flag.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useSessionStore } from '@/stores/session'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

// Map (not a plain object), matching the convention used elsewhere in the
// reports screens, so eslint-plugin-security's object-injection check doesn't
// flag these action/status/mode label lookups.
const ROLLOUT_ACTION_LABELS = new Map([
    ['initialized', 'Rollout started'],
    ['updated', 'Percentage updated'],
    ['paused', 'Paused'],
    ['resumed', 'Resumed'],
    ['advanced', 'Advanced'],
    ['completed', 'Completed'],
    ['destroyed', 'Removed'],
])

export default {
    components: { ProjectChangeNotice },
    name: 'RolloutProgress',
    setup() {
        const { current } = useCurrentEnvironment()
        const { can } = usePermissions()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 50 })
        return {
            current,
            sessionStore: useSessionStore(),
            hasPlanFeature,
            canView: computed(
                () =>
                    can(PERMISSIONS.AUDIT_LOG_READ) &&
                    hasPlanFeature('advanced_reporting')
            ),
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            activeRollouts: [],
            generatedAt: null,
            flags: [],
            filters: {
                flagKey: '',
                status: '',
            },
        }
    },
    computed: {
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
    },
    watch: {
        environmentScopeKey() {
            this.refresh()
        },
    },
    methods: {
        formatMode(mode) {
            return mode === 'automatic' ? 'Automatic' : 'Manual'
        },
        modeBadgeClass(mode) {
            return mode === 'automatic'
                ? 'bg-indigo-100 text-indigo-800'
                : 'bg-teal-100 text-teal-800'
        },
        formatStatus(status) {
            return status === 'paused' ? 'Paused' : 'Active'
        },
        statusBadgeClass(status) {
            return status === 'paused'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
        },
        formatRolloutAction(action) {
            return ROLLOUT_ACTION_LABELS.get(action) || action
        },
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        load() {
            if (!this.canView) return

            this.run(
                (signal) =>
                    ReportService.getRolloutProgress(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            flagKey: this.filters.flagKey || undefined,
                            status: this.filters.status || undefined,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.activeRollouts = response.data.active_rollouts
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.activeRollouts = []
                    this.resetOnError()
                }
            )
        },
        refresh() {
            this.applyFilters()

            if (this.canView) {
                FlagService.list(this.current.projectKey)
                    .then((response) => {
                        this.flags = response.data ?? []
                    })
                    .catch(() => {
                        this.flags = []
                    })
            }
        },
    },
    mounted() {
        this.refresh()
    },
}
</script>
